import { useEffect, useContext } from 'react';

import type { Provider } from '@atlaskit/collab-provider';
import type { CollabEditProvider } from '@atlaskit/editor-common/collab';

import { COLLAB_EDIT_PROVIDER_MEASURES } from '@confluence/fabric-providers/entry-points/CollabEditProviderMeasures.ts';
import { CollabEditProviderMeasures } from '@confluence/action-measures/entry-points/CollabEditProviderMeasures.ts';
import { EDIT_PAGE_LOAD_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';

import { useNativeCollabConnected } from './useNativeCollabState.ts';
import { nativeCollabPermissionsTokenState } from './CollabPermissionsTokenState.ts';

/**
 * Listens to passed in collab provider connected event and returns isCollabProviderConnected boolean.
 *
 * @param onConnected - Optional callback invoked once when the collab provider fires `connected`.
 */
export const useConnected = (
	collabEditProvider: (CollabEditProvider & Provider) | null,
	contentId: string,
	onConnected?: () => void,
) => {
	const [{ collabEditConnected }, nativeCollabActions] = useNativeCollabConnected();
	const experienceTracker = useContext(ExperienceTrackerContext);

	useEffect(() => {
		if (!collabEditProvider) {
			return;
		}
		const handleConnected = () => {
			nativeCollabActions.setCollabEditConnected(true);
			nativeCollabPermissionsTokenState.clearPermissionsTokenState(contentId);
			CollabEditProviderMeasures.markMeasureEnd(
				COLLAB_EDIT_PROVIDER_MEASURES.COLLAB_EDIT_PROVIDER_CONNECTED,
			);
			// tracking time between when 'init' event is fired and connection has been established
			CollabEditProviderMeasures.markMeasureEnd(
				COLLAB_EDIT_PROVIDER_MEASURES.COLLAB_EDIT_PROVIDER_INIT_DOC,
			);
			experienceTracker.attachDebugPoint({
				name: EDIT_PAGE_LOAD_EXPERIENCE,
				message: 'NCS Connected',
			});
			onConnected?.();
		};
		collabEditProvider.on('connected', handleConnected);
		return () => {
			collabEditProvider.off('connected', handleConnected);
		};
	}, [collabEditProvider, nativeCollabActions, contentId, experienceTracker, onConnected]);

	return { isCollabProviderConnected: collabEditConnected };
};
